import {
  Badge,
  Button,
  ButtonList,
  DropdownButton,
  Page,
  SearchInput,
  Table,
} from '@dixa/lib-shared-habla';
import Fuse from 'fuse.js';
import { produce } from 'immer';
import { ampli } from 'lib-shared-tracking';
import { useMemo, useRef, useState } from 'react';

import { presenceApi } from 'apis/presence';
import { appActions } from 'app/app.actions';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { DragMoveEvent } from 'utils/useOrderedDragDrop';

import { AwayStatusModal } from './components/modal/awayStatusModal';
import { AwayStatusRow } from './components/row/awayStatusRow';

const orderIndexStep = 10;

const filterOptions = {
  all: 'All away statuses',
  active: 'Active away statuses',
  inactive: 'Inactive away statuses',
} as const;

type FilterOption = keyof typeof filterOptions;

export const SettingsAwayStatusView = (): JSX.Element => {
  const dispatch = useAppDispatch();

  const [searchTerm, setSearchTerm] = useState('');

  const [filterMode, setFilterMode] = useState<FilterOption>('active');
  const recentlyToggledStatuses = useRef<Set<string>>(new Set([]));

  const { awayStatuses, isLoading, isError, isSuccess, refetch } =
    presenceApi.useReadAwayStatusesQuery(undefined, {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        awayStatuses: data?.data.customAwayStatuses ?? [],
      }),
    });
  const itemOrderIndexIsSequential = useMemo(
    () =>
      awayStatuses.every(
        ({ order_index, is_deactivated }, index) =>
          is_deactivated || order_index === index * orderIndexStep,
      ),
    [awayStatuses],
  );

  const filteredAwayStatuses = useMemo(
    () =>
      awayStatuses.filter((awayStatus) => {
        if (recentlyToggledStatuses.current.has(awayStatus.id)) {
          return true;
        }

        switch (filterMode) {
          case 'all':
            return true;
          case 'inactive':
            return awayStatus.is_deactivated;
          default:
            return !awayStatus.is_deactivated;
        }
      }),

    [filterMode, awayStatuses],
  );

  const searchAwayStatuses = useMemo(() => {
    const minMatchCharLength = 2;

    if (!searchTerm || searchTerm.length < minMatchCharLength) {
      return filteredAwayStatuses;
    }

    const fuse = new Fuse(filteredAwayStatuses, {
      keys: ['id', 'label', 'description'],
      threshold: 0.2,
      minMatchCharLength,
      distance: 200,
    });

    return fuse.search(searchTerm).map(({ item }) => item);
  }, [filteredAwayStatuses, searchTerm]);

  const handleStatusMove = async ({ dragIndex, hoverIndex }: DragMoveEvent) => {
    const newOrderIndex =
      (filteredAwayStatuses[hoverIndex].order_index ?? 0) +
      (dragIndex < hoverIndex ? 1 : -1);

    // If the order_index is not sequential, we need to do a full recompute
    // otherwise we can just update the order_index of the dragged attribute
    const startIndex = itemOrderIndexIsSequential
      ? Math.min(dragIndex, hoverIndex)
      : 0;
    const endIndex = itemOrderIndexIsSequential
      ? Math.max(dragIndex, hoverIndex)
      : filteredAwayStatuses.length - 1;

    const tempStatuses = produce(filteredAwayStatuses, (draft) => {
      draft[dragIndex].order_index = newOrderIndex;
      draft.sort((a, b) => a.order_index - b.order_index);
    });

    let count = startIndex;
    const patch = {};
    for (let i = startIndex; i <= endIndex; i++) {
      const currentStatus = tempStatuses[i];
      if (currentStatus.is_deactivated) {
        continue;
      }

      patch[currentStatus.id] = count * orderIndexStep;

      // Keep track of the count for the next active attribute
      count++;
    }

    await dispatch(
      presenceApi.endpoints.updateAwayStatusesOrdering.initiate(patch),
    );
  };

  const handleCreateAwayStatus = () => {
    ampli.settingsCustomAwayStatusAddNewClicked();
    dispatch(
      appActions.showModal({
        modal: {
          type: 'settings:awayStatus',
          arg: {},
        },
      }),
    );
  };

  return (
    <Page.Section
      title="All away statuses"
      titleMeta={
        awayStatuses.length > 0 && (
          <Badge size="small" variant="secondary">
            {awayStatuses.length}
          </Badge>
        )
      }
      controls={
        <>
          <SearchInput
            onSubmit={setSearchTerm}
            onToggle={(isOpen) => !isOpen && setSearchTerm('')}
          />

          <DropdownButton
            dropdown={
              <ButtonList>
                {Object.keys(filterOptions).map((key) => (
                  <ButtonList.Item
                    key={key}
                    isSelected={filterMode === key}
                    onClick={() => {
                      setFilterMode(key as FilterOption);
                      recentlyToggledStatuses.current.clear();
                    }}
                  >
                    {filterOptions[key]}
                  </ButtonList.Item>
                ))}
              </ButtonList>
            }
          >
            {filterOptions[filterMode]}
          </DropdownButton>

          <Button variant="primary" onClick={handleCreateAwayStatus}>
            Create new status
          </Button>
        </>
      }
    >
      <Table
        stickyHeader
        stickyFooter
        loadingFailed={isError}
        onLoadingRetry={refetch}
        emptyState={
          !searchAwayStatuses.length && isSuccess
            ? {
                title: 'No away statuses found',
                description: 'Try changing your search criteria.',
              }
            : undefined
        }
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Status label</Table.Th>
            <Table.Th width="0">Active</Table.Th>

            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {searchAwayStatuses.map((awayStatus, index) => (
            <AwayStatusRow
              key={awayStatus.id}
              awayStatus={awayStatus}
              index={index}
              isDragDisabled={
                isLoading || awayStatus.is_deactivated || !!searchTerm
              }
              isLoading={isLoading}
              onActiveStateChange={(id) =>
                recentlyToggledStatuses.current.add(id)
              }
              onMove={handleStatusMove}
            />
          ))}
        </Table.Tbody>
      </Table>

      <AwayStatusModal />
    </Page.Section>
  );
};
